import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { useFragment } from '@apollo/client'
import { Box, GridBoxListItem as Item } from '@factorialco/deprecated-design-system'
import { PreviewLegalEntityFragmentDoc } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import type { Option } from 'modules/core/components/Popover/Content'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

interface Props {
  legalEntityId: string
  legalEntitiesCount: number
  extra?: Record<string, string | string>
}

const LegalEntityItem = ({ legalEntityId, legalEntitiesCount, extra }: Props) => {
  const { data } = useFragment({
    fragment: PreviewLegalEntityFragmentDoc,
    fragmentName: 'PreviewLegalEntity',
    from: {
      __typename: 'CompaniesLegalEntity',
      id: legalEntityId ?? '',
    },
  })

  const isMainLegalEntity = !!data.main
  const legalName = data.legalName ?? ''

  const activeEmployeesCount = useMemo(
    () =>
      data.employeeGroups?.reduce(
        (total, group) => total + (group?.employeesConnection?.totalCount ?? 0),
        0
      ) ?? 0,
    [data]
  )

  const { navigation } = useNavigation()

  const navigate = useNavigate()

  const getOptions = () => {
    const options: Option[] = []

    if (legalEntitiesCount > 1 && !isMainLegalEntity) {
      options.push({
        onClick: removeLegalEntity,
        title: i18n.t('settings.legal_entities.remove'),
        type: 'wrong',
      })
    }

    return options
  }

  const removeLegalEntity = () => {
    navigate(navigation.settings.details.removeLegalEntityId.fullPath({ id: legalEntityId }))
  }

  const goToLegalEntity = () => {
    navigate(navigation.settings.legalEntitiesId.fullPath({ id: legalEntityId }))
  }

  return (
    <Item
      actionText={i18n.t('settings.legal_entities.list.item.actionText')}
      illustrationName="building"
      options={getOptions()}
      onClickAction={goToLegalEntity}
      title={legalName}
      extra={extra}
    >
      <Box>
        {i18n.tp('settings.legal_entities.list.item.employees', {
          count: activeEmployeesCount,
        })}
      </Box>
    </Item>
  )
}

export default observer(LegalEntityItem)
